import { z } from 'zod'

// A datetime that may be absent (null, undefined, or '') — e.g. a location that has not yet been
// arrived at or departed from. `z.coerce.date().nullable()` only tolerates `null`: for `undefined`
// or `''` it runs `new Date(undefined)`/`new Date('')` → Invalid Date → an `invalid_date` error.
// Normalise those to null before coercing so omitted fields from the API validate cleanly.
export const nullableDate = z.preprocess(
  (value) => (value == null || value === '' ? null : value),
  z.coerce.date().nullable(),
)

export const TaskStatusEnum = z.enum([
  'REQUESTED',
  'REJECTED',
  'SCHEDULED',
  'IN_PROGRESS',
  'DELAYED',
  'COMPLETED',
  'CANCELLED',
])

export const Task = z.object({
  id: z.string(),
  reference: z.string().optional(),
  description: z.string().optional(),
  fromLocation: z.string(),
  toLocation: z.string(),
  issuer: z.object({
    id: z.number(),
    name: z.string(),
    avatar: z.string().nullable(),
  }),
  assignedTo: z
    .object({
      id: z.number(),
      name: z.string(),
      avatar: z.string().nullable(),
    })
    .nullable()
    .optional(),
  status: TaskStatusEnum,
})

export const TaskDefinition = z.object({
  id: z.string(),
  taskDefinition: z.object({
    id: z.string(),
    reference: z.string().optional(),
    description: z.string().optional(),
    locations: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        address: z.string(),
        type: z.string(),
        predecessor: z.string().nullable(),
        originalObjectId: z.union([z.string(), z.number()]).nullable(),
        geoJson: z.any().nullable(),
        enabledAlert: z.boolean().default(false),
        // The task's schedule comes from the first and last of these (see getTaskSchedule).
        plannedArrival: nullableDate,
        actualArrival: nullableDate,
        actualDepartureDatetime: nullableDate,
      }),
    ),
  }),
  assignedByOrg: z.object({
    id: z.number(),
    name: z.string(),
    avatar: z.string().nullable(),
  }),
  onwardAssignedToOrg: z
    .object({
      id: z.number(),
      name: z.string(),
      avatar: z.string().nullable(),
    })
    .nullable(),
  status: TaskStatusEnum,
  assignedVehicle: z
    .object({
      id: z.number(),
      plateNumber: z.string().nullable(),
      telemetryFleetId: z.string().nullable(),
    })
    .nullable(),
})

export const TaskVehicleAvailabilityDefinition = z.object({
  id: z.number(),
  plateNumber: z.string().nullable(),
  telemetryFleetId: z.string().nullable(),
  assignedTasks: z.array(
    z.object({
      status: TaskStatusEnum,
      assignedByOrg: z.object({
        id: z.number(),
        name: z.string(),
        avatar: z.string().nullable(),
      }),
      taskDefinition: z.object({
        id: z.string(),
        reference: z.string().optional(),
        description: z.string().optional(),
        locations: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            address: z.string(),
            type: z.string(),
            predecessor: z.string().nullable(),
            originalObjectId: z.union([z.string(), z.number()]).nullable(),
            geoJson: z.any().nullable(),
            enabledAlert: z.boolean().default(false),
            plannedArrival: nullableDate,
            actualArrival: nullableDate,
            actualDepartureDatetime: nullableDate,
          }),
        ),
      }),
    }),
  ),
})

export type Task = z.infer<typeof Task>
export type TaskDefinition = z.infer<typeof TaskDefinition>
export type TaskVehicleAvailabilityDefinition = z.infer<typeof TaskVehicleAvailabilityDefinition>
